/**
 * Super Admin Service
 * 
 * Platform-level administration capabilities:
 * - Manage all workspaces
 * - Impersonate users (for support)
 * - System-wide analytics
 * - Feature flag management
 * 
 * NOTE: Super admin is SEPARATE from workspace admin.
 * Workspace admins manage their workspace; super admins manage the platform.
 */

import { getSupabaseClient } from './supabaseClient';
import { createEphemeralSupabaseClient } from './ephemeralSupabaseClient';
// getOrCreateSupabaseUser removed — Clerk is disabled; callers pass Supabase UUIDs directly.
import type { Workspace, WorkspacePlan, WorkspaceStatus } from './workspaceService';

// ============================================================================
// Types
// ============================================================================

export interface SuperAdmin {
  id: string;
  userId: string;
  canCreateWorkspaces: boolean;
  canImpersonateUsers: boolean;
  canAccessAllWorkspaces: boolean;
  canManageBilling: boolean;
  grantedBy?: string;
  grantedAt: string;
  createdAt: string;
}

export interface PlatformStats {
  totalWorkspaces: number;
  activeWorkspaces: number;
  demoWorkspaces: number;
  totalUsers: number;
  totalCandidates: number;
  totalProcesses: number;
  workspacesByPlan: Record<WorkspacePlan, number>;
}

export interface WorkspaceOverview extends Workspace {
  memberCount: number;
  candidateCount: number;
  processCount: number;
  ownerEmail?: string;
}

export type TenantPlan = 'free' | 'starter' | 'pro' | 'enterprise';
export type TenantStatus = 'active' | 'suspended' | 'pending';

export interface TenantOverview {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  plan: TenantPlan;
  status: TenantStatus;
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  workspaceCount: number;
}

// ============================================================================
// Super Admin Checks
// ============================================================================

/**
 * Check if a user is a super admin
 */
export async function isSuperAdmin(clerkUserId: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  try {
    const { data, error } = await supabase
      .from('super_admins')
      .select('id')
      .eq('user_id', clerkUserId)
      .maybeSingle();
    return !error && !!data;
  } catch {
    return false;
  }
}

/**
 * Check if a user is a super admin by Supabase user id (no Clerk).
 */
export async function isSuperAdminBySupabaseUserId(supabaseUserId: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('super_admins')
    .select('id')
    .eq('user_id', supabaseUserId)
    .maybeSingle();
  return !error && !!data;
}

/**
 * Whether the person signed in right now is a platform admin, as the server
 * answers it. The Platform Admin site's front door asks this. The id comes from
 * the auth server, not from the browser's stored user, and the answer is the
 * same check the database uses for everything inside the site. Any failure to
 * answer counts as no.
 */
export async function isSignedInPersonPlatformAdmin(): Promise<boolean> {
  const supabase = getSupabaseClient();
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) return false;
    const { data, error } = await supabase.rpc('is_platform_admin', { p_user_id: user.id });
    return !error && data === true;
  } catch {
    return false;
  }
}

/**
 * Get super admin record for a user
 */
export async function getSuperAdmin(clerkUserId: string): Promise<SuperAdmin | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('super_admins')
    .select('*')
    .eq('user_id', clerkUserId)
    .maybeSingle();
  
  if (error || !data) return null;
  
  return {
    id: data.id,
    userId: data.user_id,
    canCreateWorkspaces: data.can_create_workspaces,
    canImpersonateUsers: data.can_impersonate_users,
    canAccessAllWorkspaces: data.can_access_all_workspaces,
    canManageBilling: data.can_manage_billing,
    grantedBy: data.granted_by,
    grantedAt: data.granted_at,
    createdAt: data.created_at,
  };
}

// ============================================================================
// Platform Statistics
// ============================================================================

/**
 * Get platform-wide statistics (super admin only)
 */
export async function getPlatformStats(): Promise<PlatformStats> {
  const supabase = getSupabaseClient();
  
  // Get workspace counts by status and plan
  const { data: workspaces } = await supabase
    .from('workspaces')
    .select('id, status, plan, is_demo');
  
  const { count: userCount } = await supabase
    .from('users')
    .select('id', { count: 'exact', head: true });
  
  const { count: candidateCount } = await supabase
    .from('items')
    .select('id', { count: 'exact', head: true });
  
  const { count: processCount } = await supabase
    .from('degap_cycles')
    .select('id', { count: 'exact', head: true });
  
  const workspaceList = workspaces || [];
  
  const workspacesByPlan: Record<WorkspacePlan, number> = {
    free: 0,
    starter: 0,
    professional: 0,
    enterprise: 0,
  };
  
  workspaceList.forEach(ws => {
    workspacesByPlan[ws.plan as WorkspacePlan] = (workspacesByPlan[ws.plan as WorkspacePlan] || 0) + 1;
  });
  
  return {
    totalWorkspaces: workspaceList.length,
    activeWorkspaces: workspaceList.filter(ws => ws.status === 'active').length,
    demoWorkspaces: workspaceList.filter(ws => ws.is_demo).length,
    totalUsers: userCount || 0,
    totalCandidates: candidateCount || 0,
    totalProcesses: processCount || 0,
    workspacesByPlan,
  };
}

// ============================================================================
// Workspace Management (Super Admin)
// ============================================================================

/**
 * List all workspaces with details (super admin only)
 */
export async function listAllWorkspaces(
  filters?: {
    status?: WorkspaceStatus;
    plan?: WorkspacePlan;
    isDemo?: boolean;
  }
): Promise<WorkspaceOverview[]> {
  const supabase = getSupabaseClient();
  
  let query = supabase
    .from('workspaces')
    .select(`
      *,
      users!workspaces_owner_id_fkey(email, name)
    `)
    .order('created_at', { ascending: false });
  
  if (filters?.status) {
    query = query.eq('status', filters.status);
  }
  if (filters?.plan) {
    query = query.eq('plan', filters.plan);
  }
  if (filters?.isDemo !== undefined) {
    query = query.eq('is_demo', filters.isDemo);
  }
  
  const { data, error } = await query;
  
  if (error) throw error;
  
  // Get counts for each workspace
  const workspaceIds = (data || []).map(w => w.id);
  
  const { data: memberCounts } = await supabase
    .from('workspace_memberships')
    .select('workspace_id')
    .in('workspace_id', workspaceIds);
  
  const { data: candidateCounts } = await supabase
    .from('items')
    .select('workspace_id')
    .in('workspace_id', workspaceIds);
  
  const { data: processCounts } = await supabase
    .from('degap_cycles')
    .select('workspace_id')
    .in('workspace_id', workspaceIds);
  
  // Count by workspace
  const memberCountMap = new Map<string, number>();
  const candidateCountMap = new Map<string, number>();
  const processCountMap = new Map<string, number>();
  
  (memberCounts || []).forEach(m => {
    memberCountMap.set(m.workspace_id, (memberCountMap.get(m.workspace_id) || 0) + 1);
  });
  (candidateCounts || []).forEach(c => {
    candidateCountMap.set(c.workspace_id, (candidateCountMap.get(c.workspace_id) || 0) + 1);
  });
  (processCounts || []).forEach(p => {
    processCountMap.set(p.workspace_id, (processCountMap.get(p.workspace_id) || 0) + 1);
  });
  
  return (data || []).map(row => ({
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    logoUrl: row.logo_url,
    plan: row.plan,
    planExpiresAt: row.plan_expires_at,
    features: row.features || {},
    settings: row.settings || {},
    status: row.status,
    isDemo: row.is_demo,
    ownerId: row.owner_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ownerEmail: row.users?.email,
    memberCount: memberCountMap.get(row.id) || 0,
    candidateCount: candidateCountMap.get(row.id) || 0,
    processCount: processCountMap.get(row.id) || 0,
  }));
}

/**
 * Update workspace status (super admin only)
 */
export async function updateWorkspaceStatus(
  workspaceId: string,
  status: WorkspaceStatus
): Promise<void> {
  const supabase = getSupabaseClient();
  
  const { error } = await supabase
    .from('workspaces')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', workspaceId);
  
  if (error) throw error;
}

/**
 * Update workspace plan (super admin only)
 */
export async function updateWorkspacePlan(
  workspaceId: string,
  plan: WorkspacePlan,
  expiresAt?: string
): Promise<void> {
  const supabase = getSupabaseClient();
  
  const { error } = await supabase
    .from('workspaces')
    .update({ 
      plan, 
      plan_expires_at: expiresAt,
      updated_at: new Date().toISOString() 
    })
    .eq('id', workspaceId);
  
  if (error) throw error;
}

/**
 * Delete a workspace (super admin only)
 * This is a soft delete - sets status to 'archived'
 */
export async function archiveWorkspace(workspaceId: string): Promise<void> {
  await updateWorkspaceStatus(workspaceId, 'archived');
}

/**
 * Hard delete a workspace and all its data (super admin only)
 * USE WITH EXTREME CAUTION
 */
export async function deleteWorkspace(workspaceId: string): Promise<void> {
  const supabase = getSupabaseClient();
  
  // Delete in order to respect foreign keys
  await supabase.from('workspace_memberships').delete().eq('workspace_id', workspaceId);
  await supabase.from('items').delete().eq('workspace_id', workspaceId);
  await supabase.from('idea_submissions').delete().eq('workspace_id', workspaceId);
  const { data: cycleIds } = await supabase.from('degap_cycles').select('id').eq('workspace_id', workspaceId);
  if (cycleIds?.length) {
    await supabase.from('prioritization_sessions').delete().in('cycle_id', cycleIds.map((c) => c.id));
  }
  await supabase.from('degap_cycles').delete().eq('workspace_id', workspaceId);
  await supabase.from('item_pools').delete().eq('workspace_id', workspaceId);
  
  const { error } = await supabase
    .from('workspaces')
    .delete()
    .eq('id', workspaceId);
  
  if (error) throw error;
}

// ============================================================================
// Super Admin Management
// ============================================================================

/**
 * Make someone a platform admin. Only the Platform Admin site calls this.
 *
 * Adding a platform admin goes through `elevate_admin` (migration 803), and the
 * server only allows it for a platform admin who signed in within the last ten
 * minutes — it reads the sign-in time from the session itself, so this cannot
 * be skipped from the browser. The database refuses every direct write to
 * `super_admins`; that function is the only way in.
 *
 * So the person re-enters their password, and the grant is made on the session
 * that password just opened: a throwaway client signs in, makes the call, and
 * is signed out again. The session the app is running on is never touched —
 * a wrong password leaves the person exactly where they were.
 *
 * It calls the function directly rather than through `elevateAdmin` in
 * delegationRpc.ts on purpose: that wrapper is what the normal product's role
 * screens use, and its type does not admit PLATFORM_ADMIN.
 *
 * A refusal comes back as a sentence and is thrown here as-is. There is no
 * revoke in the product: `super_admins` refuses client deletes the same way
 * and no function removes a row.
 */
export async function addPlatformAdmin(targetUserId: string, password: string): Promise<void> {
  const { data: current, error: userError } = await getSupabaseClient().auth.getUser();
  const email = current?.user?.email;
  if (userError || !email) {
    throw new Error('You are not signed in. Sign in again, then retry.');
  }

  const fresh = createEphemeralSupabaseClient();
  const { data: signedIn, error: signInError } = await fresh.auth.signInWithPassword({ email, password });
  if (signInError || !signedIn.session) {
    throw new Error('That password was not accepted. Nothing was changed.');
  }

  try {
    const { data, error } = await fresh.rpc('elevate_admin', {
      p_target_user_id: targetUserId,
      p_admin_role: 'PLATFORM_ADMIN',
      p_scope_id: null,
    });
    if (error) throw new Error(error.message);
    const result = (data ?? {}) as { success?: boolean; error?: string };
    if (!result.success) {
      throw new Error(result.error ?? 'The platform admin grant was refused.');
    }
  } finally {
    // End only the throwaway session; the app's own session stays as it was.
    await fresh.auth.signOut({ scope: 'local' }).catch(() => undefined);
  }
}

/**
 * Find a user by email (Supabase users table). Returns id or null.
 */
export async function findUserByEmail(email: string): Promise<{ id: string; name: string | null; email: string } | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('users')
    .select('id, name, email')
    .ilike('email', email.trim())
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return { id: data.id, name: data.name ?? null, email: data.email ?? '' };
}

// ============================================================================
// Tenant Listing (Super Admin)
// ============================================================================

/**
 * List all tenants with workspace count (from DB only).
 */
export async function listAllTenants(): Promise<TenantOverview[]> {
  const supabase = getSupabaseClient();
  const { data: tenants, error } = await supabase
    .from('tenants')
    .select('id, name, slug, description, plan, status, settings, created_at, updated_at')
    .order('name');

  if (error) throw error;

  const tenantIds = (tenants || []).map((t) => t.id);
  const { data: wsCounts } = await supabase
    .from('workspaces')
    .select('tenant_id')
    .in('tenant_id', tenantIds);

  const countByTenant = new Map<string, number>();
  (wsCounts || []).forEach((row) => {
    countByTenant.set(row.tenant_id, (countByTenant.get(row.tenant_id) || 0) + 1);
  });

  return (tenants || []).map((row) => ({
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description ?? null,
    plan: (row.plan || 'starter') as TenantPlan,
    status: (row.status || 'active') as TenantStatus,
    settings: (row.settings as Record<string, unknown>) || {},
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    workspaceCount: countByTenant.get(row.id) || 0,
  }));
}

// ============================================================================
// Tenant Creation (Super Admin)
// ============================================================================

export interface CreateTenantInput {
  name: string;
  slug: string;
  description?: string;
  plan?: TenantPlan;
}

export interface CreateTenantResult {
  tenant_id: string;
  workspace_id: string;
  root_org_unit_id: string;
}

/**
 * Create a new tenant and its default workspace (super admin only).
 * Caller must be a platform admin. Creator is added as tenant member and workspace owner.
 * Uses create_tenant_bootstrap RPC (tenant, workspace, memberships, root org unit).
 */
export async function createTenantAndDefaultWorkspace(
  input: CreateTenantInput,
  creatorSupabaseUserId: string
): Promise<CreateTenantResult> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.rpc('create_tenant_bootstrap', {
    p_tenant_name: input.name,
    p_tenant_slug: input.slug || input.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 100),
    p_workspace_name: `${input.name} Workspace`,
    p_owner_user_id: creatorSupabaseUserId,
  });

  if (error) throw error;

  return data;
}

/**
 * List all super admins
 */
export async function listSuperAdmins(): Promise<(SuperAdmin & { userEmail: string })[]> {
  const supabase = getSupabaseClient();

  const { data: rows, error } = await supabase
    .from('super_admins')
    .select('*')
    .order('created_at');

  if (error) throw error;

  const userIds = (rows || []).map((r) => r.user_id).filter(Boolean);
  const emailMap = new Map<string, string>();
  if (userIds.length > 0) {
    const { data: userRows } = await supabase
      .from('users')
      .select('id, email')
      .in('id', userIds);
    (userRows || []).forEach((u) => emailMap.set(u.id, u.email ?? ''));
  }

  return (rows || []).map((row) => ({
    id: row.id,
    userId: row.user_id,
    canCreateWorkspaces: row.can_create_workspaces,
    canImpersonateUsers: row.can_impersonate_users,
    canAccessAllWorkspaces: row.can_access_all_workspaces,
    canManageBilling: row.can_manage_billing,
    grantedBy: row.granted_by,
    grantedAt: row.granted_at,
    createdAt: row.created_at,
    userEmail: emailMap.get(row.user_id) ?? '',
  }));
}

// ============================================================================
// User Impersonation (for support)
// ============================================================================

export interface ImpersonationSession {
  originalUserId: string;
  impersonatedUserId: string;
  workspaceId?: string;
  startedAt: string;
  reason: string;
}

let currentImpersonation: ImpersonationSession | null = null;

/**
 * Start impersonating a user (for support purposes)
 */
export function startImpersonation(
  originalClerkUserId: string,
  targetUserId: string,
  reason: string,
  workspaceId?: string
): ImpersonationSession {
  currentImpersonation = {
    originalUserId: originalClerkUserId,
    impersonatedUserId: targetUserId,
    workspaceId,
    startedAt: new Date().toISOString(),
    reason,
  };
  
  console.warn('[SuperAdmin] IMPERSONATION STARTED:', currentImpersonation);
  
  // TODO: Log to audit_events table
  
  return currentImpersonation;
}

/**
 * End impersonation session
 */
export function endImpersonation(): void {
  if (currentImpersonation) {
    console.warn('[SuperAdmin] IMPERSONATION ENDED:', currentImpersonation);
    currentImpersonation = null;
  }
}

/**
 * Get current impersonation session
 */
export function getImpersonationSession(): ImpersonationSession | null {
  return currentImpersonation;
}

/**
 * Check if currently impersonating
 */
export function isImpersonating(): boolean {
  return currentImpersonation !== null;
}
